import React, { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  approvedCtaFromApi,
  isAwaitingConformityStatus,
  OV,
  PAYMENT_CONFIRMING_CHROME,
  resolveBoardState,
  variantFromApi,
} from '../boardStates';
import { Stepper } from '../board/Stepper';
import { StatusPill } from '../board/StatusPill';
import { SummaryCard } from '../board/SummaryCard';
import { AgentCard } from '../board/AgentCard';
import { StatePanel } from '../board/StatePanels';
import { usePortalNavigation } from '../hooks/usePortalQuery';
import { usePortalOffer } from '../hooks/usePortalOffer';
import { clearOfferSignatureMark, isAwaitingOfferSignature } from '../api/offerSignatureService';
import {
  clearPaymentMark,
  handoffToPaymentUrl,
  isSafePaymentUrl,
  PAYMENT_CONFIRM_WAIT_MS,
  paymentStartedAt,
} from '../api/paymentHandoff';
import { usePortalRequirements } from '../hooks/usePortalRequirements';
import { portalStatusQueryKey, usePortalStatus } from '../hooks/usePortalStatus';
import { usePortalCopy, usePortalUi } from '../PortalUi';
import { PORTAL_FIXTURE } from '../mock/portalFixture';
import { PortalButton, PortalModal, PortalModalSpacer } from '../ui';
import { takeApplicationSignatureMark } from '../../utils/applicationSignatureMark';
import {
  coverageFrom,
  firstNameFrom,
  formatTermYears,
  OfferOutcome,
  postConfirm,
  offerRequiresSignature,
  postOfferOutcome,
} from '../api/portalService';

/** Cada cuánto se vuelve a preguntar mientras ASA resuelve. */
const PROCESSING_POLL_MS = 2000;
/**
 * Pasado el primer minuto, ASA ya no es «cuestión de segundos»: su cliente
 * tiene 10 s de timeout, así que lo normal resuelve mucho antes. A partir de
 * acá el sondeo se espacia — el cliente se queda igual en el tablero
 * (OUW-98), pero sin gastar una consulta cada 2 s indefinidamente.
 */
const PROCESSING_SLOW_AFTER_MS = 60000;
const PROCESSING_SLOW_POLL_MS = 10000;
/** A partir de acá el panel deja de prometer segundos y dice la verdad. */
const PROCESSING_STALLED_AFTER_MS = 180000;
/**
 * OUW-181: la propuesta de la contraoferta se arma en segundo plano al emitirse
 * la oferta. Mientras no esté, el panel espera y el aceptar está bloqueado, así
 * que hay que volver a preguntar: sin esto el cliente se queda en el spinner
 * hasta que recargue a mano.
 */
const OFFER_DOCUMENT_POLL_MS = 3000;
/**
 * OUW-186: el cliente vuelve de Logalty antes que el veredicto. Mientras la
 * firma se registra, el tablero espera y vuelve a preguntar por el ESTADO (no
 * por la oferta): lo que tiene que cambiar es la solicitud.
 */
const OFFER_SIGNATURE_POLL_MS = 3000;
/**
 * OUW-67: el cliente vuelve de ole-web-payment antes que el aviso del primer
 * pago. Con tarjeta el aviso tarda segundos o minutos: los primeros 5 minutos
 * se pregunta seguido; después, espaciado (con transferencia pueden ser días).
 */
const PAYMENT_POLL_MS = 10000;
const PAYMENT_FAST_FOR_MS = 5 * 60 * 1000;
const PAYMENT_SLOW_POLL_MS = 60000;
/**
 * Tras confirmar, `register_policy` corre en el outbox y el link aparece
 * recién con la póliza aprobada. El GET inmediato no lo trae. Se vuelve a
 * preguntar con espera lineal (2 s, 4 s, 6 s…) hasta 30 s.
 */
export const PAYMENT_LINK_POLL_BASE_MS = 2000;
export const PAYMENT_LINK_POLL_STEP_MS = 2000;
export const PAYMENT_LINK_POLL_MAX_MS = 30000;
/**
 * Por encima de los 5 intentos del outbox (~7,5 min). Al cumplirse, el
 * spinner se apaga: el botón queda deshabilitado como si el link no fuera
 * a llegar. El sondeo sigue en el tope, por si la póliza se registra después.
 */
export const PAYMENT_LINK_STALL_MS = 10 * 60 * 1000;

export function BoardPage() {
  const { query } = usePortalNavigation();
  const t = usePortalCopy();
  const queryClient = useQueryClient();
  const { setOfferOutcome, setConfirmSession, setLiveOffer, setLiveStatus, setRequirementRefetch } = usePortalUi();
  const mocked = Boolean(query.state);
  const live = usePortalStatus(query.quoteID, { enabled: !mocked });
  // OUW-276: la primera visita después de firmar la solicitud lo reconoce con un aviso. La marca la
  // puso /logalty al mandar a firmar; leerla la consume, así no vuelve al recargar.
  const [signedNotice, setSignedNotice] = useState(false);
  useEffect(() => {
    if (!mocked && takeApplicationSignatureMark(query.quoteID)) setSignedNotice(true);
  }, [mocked, query.quoteID]);
  const application = mocked ? null : live.data;
  const offer = usePortalOffer(query.quoteID, {
    enabled: !mocked && application?.status === 'offer_issued',
  });
  const state = mocked
    ? resolveBoardState(query.state)
    : application
      ? variantFromApi(application)
      : 'review';
  const requirements = usePortalRequirements(query.quoteID, {
    enabled: !mocked && (state === 'initial' || state === 'uwreq'),
  });
  const chrome = OV[state];
  const approvedCta = application
    ? approvedCtaFromApi({ status: application.status, paymentUrl: application.paymentUrl })
    : undefined;
  const helloName = application ? firstNameFrom(application.applicantName) : PORTAL_FIXTURE.firstName;
  const ref = application?.reference || query.quoteID || PORTAL_FIXTURE.applicationNumber;
  const coverage = application ? coverageFrom(application) : PORTAL_FIXTURE.coverage;
  const term = application ? formatTermYears(application.termYears) : PORTAL_FIXTURE.termYears;

  useEffect(() => {
    setLiveOffer(offer.data, offer.labels);
    return () => setLiveOffer(null);
  }, [offer.data, offer.labels, setLiveOffer]);

  useEffect(() => {
    setLiveStatus(application);
    return () => setLiveStatus(null);
  }, [application, setLiveStatus]);

  /**
   * «Solicitud en proceso»: la firma acaba de disparar el envío y ASA corre
   * asíncrono. Se vuelve a preguntar cada 2 s hasta que la máquina se mueva.
   *
   * OUW-98: el board es el único destino MX post-ASA. Si ASA tarda o sigue
   * «en proceso», el cliente se queda acá — el tablero pinta el estado
   * cuando llegue (aprobada / negada / requisitos). Nunca se desvía a
   * `/congratulations`.
   */
  const processing = !mocked && state === 'processing';
  /** Ritmo del sondeo: los primeros 60 s rápido, después espaciado. */
  const [processingSlow, setProcessingSlow] = useState(false);
  /** ASA lleva demasiado: el panel deja de prometer segundos. */
  const [processingStalled, setProcessingStalled] = useState(false);

  /* El reloj de las fases, separado del sondeo para no reiniciarlo en cada
     cambio de ritmo. */
  /**
   * OUW-325: recién aprobada (típico de ASA) la solicitud pasa unos segundos
   * en `approved` hasta que el outbox la mueve a la conformidad; mientras
   * tanto «Confirmar» se ve deshabilitado. Se sondea con el mismo ritmo que
   * «en proceso». `processingStalled` sigue siendo solo de «en proceso».
   */
  const awaitingConformity = !mocked && isAwaitingConformityStatus(application?.status || '');
  const pollingStatus = processing || awaitingConformity;

  useEffect(() => {
    if (!pollingStatus) {
      setProcessingSlow(false);
      return undefined;
    }
    const aSlow = window.setTimeout(() => setProcessingSlow(true), PROCESSING_SLOW_AFTER_MS);
    return () => window.clearTimeout(aSlow);
  }, [pollingStatus]);

  useEffect(() => {
    if (!processing) {
      setProcessingStalled(false);
      return undefined;
    }
    const aStalled = window.setTimeout(() => setProcessingStalled(true), PROCESSING_STALLED_AFTER_MS);
    return () => window.clearTimeout(aStalled);
  }, [processing]);

  useEffect(() => {
    if (!pollingStatus) return undefined;

    const cada = processingSlow ? PROCESSING_SLOW_POLL_MS : PROCESSING_POLL_MS;
    const timer = window.setInterval(() => {
      // Con la pestaña en segundo plano no se consulta: el cliente no está
      // mirando, y al volver se refresca enseguida (listener de abajo).
      if (!document.hidden) live.refetch();
    }, cada);

    const alVolver = () => {
      if (!document.hidden) live.refetch();
    };
    document.addEventListener('visibilitychange', alVolver);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', alVolver);
    };
    // `live.refetch` es estable (useCallback en el hook); no se lista para no
    // reiniciar el intervalo en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingStatus, processingSlow]);

  const refetchStatus = live.refetch;
  const refetchOffer = offer.refetch;
  const refetchRequirements = requirements.refetch;

  // OUW-186: hay una firma en curso para esta solicitud. Se recalcula en cada
  // render porque la marca vence sola: pasado el plazo se deja de esperar y el
  // cliente recupera sus botones para reintentar.
  const [signatureTick, setSignatureTick] = useState(0);
  const awaitingSignature = Boolean(
    !mocked
      && query.quoteID
      && application?.status === 'offer_issued'
      && isAwaitingOfferSignature(query.quoteID),
  );

  useEffect(() => {
    // Si la solicitud ya se movió, la firma llegó: se limpia la marca para que
    // no quede esperando en una visita futura de esta misma pestaña.
    if (!mocked && application && application.status !== 'offer_issued') {
      clearOfferSignatureMark();
    }
  }, [mocked, application]);

  useEffect(() => {
    if (!awaitingSignature) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) refetchStatus();
      // Despierta al render para que la marca vencida deje de esperar aunque
      // el estado no cambie nunca (firma abandonada).
      setSignatureTick((n) => n + 1);
    }, OFFER_SIGNATURE_POLL_MS);
    const alVolver = () => {
      if (!document.hidden) refetchStatus();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', alVolver);
    };
    // `signatureTick` NO va en las deps: el efecto solo lo incrementa para
    // forzar el render que reevalúa la marca; listarlo rearmaría el intervalo
    // en cada tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingSignature, refetchStatus]);

  // OUW-181: hay una oferta abierta y su propuesta todavía no está. Se vuelve a
  // pedir hasta que aparezca. Igual que el poll de «en proceso»: con la pestaña
  // en segundo plano no se consulta, y al volver se refresca enseguida.
  // UWT-79: en INT no hay propuesta en PDF — no se espera ningún documento.
  const waitingOfferDocument = Boolean(offer.data) && !offer.data?.documentReady
    && offerRequiresSignature(offer.data);
  useEffect(() => {
    if (mocked || !waitingOfferDocument) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) refetchOffer();
    }, OFFER_DOCUMENT_POLL_MS);
    const alVolver = () => {
      if (!document.hidden) refetchOffer();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [mocked, waitingOfferDocument, refetchOffer]);

  // OUW-67: el cliente salió a pagar y el aviso del primer pago todavía no
  // llegó. Mientras la marca esté vigente, 03E espera en vez de volver a
  // ofrecer «Pagar», y se vuelve a preguntar por el ESTADO. Se recalcula en
  // cada render porque la marca vence sola.
  const [paymentTick, setPaymentTick] = useState(0);
  const payingSince = !mocked && query.quoteID && state === 'approved' && application?.status === 'confirmed'
    ? paymentStartedAt(query.quoteID)
    : null;
  const awaitingPayment = payingSince !== null;
  const paymentPollFast = payingSince !== null && Date.now() - payingSince < PAYMENT_FAST_FOR_MS;
  const paymentUrl = application?.paymentUrl;
  /**
   * Confirmado y todavía sin link. El poll de OUW-67 no cubre esto: esa
   * espera arranca cuando el cliente ya salió a pagar.
   */
  const waitingPaymentLink = Boolean(
    !mocked
      && application?.status === 'confirmed'
      && !isSafePaymentUrl(paymentUrl),
  );
  const [paymentLinkSince, setPaymentLinkSince] = useState<number | null>(null);
  const [paymentLinkTick, setPaymentLinkTick] = useState(0);
  useEffect(() => {
    if (!waitingPaymentLink) {
      setPaymentLinkSince(null);
      return;
    }
    setPaymentLinkSince((since) => since ?? Date.now());
  }, [waitingPaymentLink]);
  const paymentLinkStalled = paymentLinkSince !== null
    && Date.now() - paymentLinkSince >= PAYMENT_LINK_STALL_MS;
  /** Spinner del botón Pagar. Al vencer el umbral se apaga; el sondeo no. */
  const awaitingPaymentLink = waitingPaymentLink && !paymentLinkStalled;

  useEffect(() => {
    if (paymentLinkSince === null) return undefined;
    const remaining = paymentLinkSince + PAYMENT_LINK_STALL_MS - Date.now();
    if (remaining <= 0) return undefined;
    const timer = window.setTimeout(() => setPaymentLinkTick((n) => n + 1), remaining);
    return () => window.clearTimeout(timer);
  }, [paymentLinkSince, paymentLinkTick]);

  useEffect(() => {
    if (!waitingPaymentLink) return undefined;
    let attempt = 0;
    let timer = 0;
    let stopped = false;

    const delayFor = (n: number) => Math.min(
      PAYMENT_LINK_POLL_BASE_MS + n * PAYMENT_LINK_POLL_STEP_MS,
      PAYMENT_LINK_POLL_MAX_MS,
    );

    const schedule = () => {
      timer = window.setTimeout(() => {
        attempt += 1;
        // Oculta: no se consulta, pero el siguiente timeout se programa
        // igual. Si no, la cadena se corta y no vuelve a preguntar.
        if (!document.hidden) refetchStatus();
        if (!stopped) schedule();
      }, delayFor(attempt));
    };

    const alVolver = () => {
      if (document.hidden) return;
      window.clearTimeout(timer);
      attempt = 0;
      refetchStatus();
      if (!stopped) schedule();
    };

    schedule();
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [waitingPaymentLink, refetchStatus]);
  /** Hay link y el cliente ya salió a pagar. Única fuente de la espera: píldora y panel. */
  const payConfirming = awaitingPayment && approvedCta?.kind === 'pay' && approvedCta.enabled;
  const boardChrome = payConfirming ? PAYMENT_CONFIRMING_CHROME : chrome;
  /**
   * El último refresco de la espera falló. Se actualiza solo cuando un refresco
   * TERMINA: `usePortalStatus` borra el error al empezar cada uno, y sin esto el
   * aviso parpadeaba con cada consulta del sondeo (y se re-anunciaba a lectores).
   */
  const [payRefreshFailed, setPayRefreshFailed] = useState(false);
  useEffect(() => {
    if (live.refreshing) return;
    setPayRefreshFailed(payConfirming && Boolean(live.error));
  }, [payConfirming, live.refreshing, live.error]);

  useEffect(() => {
    // Llegó el aviso (03G) o la solicitud se movió: la espera terminó.
    if (!mocked && query.quoteID && application && (state === 'active' || application.status !== 'confirmed')) {
      clearPaymentMark(query.quoteID);
    }
  }, [mocked, query.quoteID, application, state]);

  useEffect(() => {
    if (!awaitingPayment) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) refetchStatus();
    }, paymentPollFast ? PAYMENT_POLL_MS : PAYMENT_SLOW_POLL_MS);
    const alVolver = () => {
      if (!document.hidden) refetchStatus();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [awaitingPayment, paymentPollFast, refetchStatus]);

  // La marca cambia sola dos veces: a los 5 min se espacia el sondeo y a las
  // 24 h vence. Cada respuesta del sondeo ya re-renderiza, pero con la pestaña
  // oculta no hay respuestas: estos dos cortes se programan aparte, y
  // `paymentTick` en las deps re-arma el siguiente cuando dispara uno.
  useEffect(() => {
    if (payingSince === null) return undefined;
    const now = Date.now();
    const next = [payingSince + PAYMENT_FAST_FOR_MS, payingSince + PAYMENT_CONFIRM_WAIT_MS].filter((at) => at > now);
    if (next.length === 0) return undefined;
    const timer = window.setTimeout(() => setPaymentTick((n) => n + 1), Math.min(...next) - now + 1);
    return () => window.clearTimeout(timer);
  }, [payingSince, paymentTick]);

  // Volver con «Atrás» desde la web de pagos puede restaurar la página tal
  // como quedó (bfcache), sin pedir nada de nuevo: se refresca a mano.
  useEffect(() => {
    if (mocked) return undefined;
    const alRestaurar = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      refetchStatus();
      setPaymentTick((n) => n + 1);
    };
    window.addEventListener('pageshow', alRestaurar);
    return () => window.removeEventListener('pageshow', alRestaurar);
  }, [mocked, refetchStatus]);

  const handlePay = useCallback(() => {
    if (handoffToPaymentUrl(paymentUrl, query.quoteID)) {
      setPaymentTick((n) => n + 1);
    }
  }, [paymentUrl, query.quoteID]);

  const refreshStatusAfterWrite = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: portalStatusQueryKey(query.quoteID) });
  }, [queryClient, query.quoteID]);

  useEffect(() => {
    if (mocked || !query.quoteID) {
      setRequirementRefetch(undefined);
      return undefined;
    }
    // Subir o borrar un requisito es una escritura, como confirmar o responder
    // la oferta: el refresco va por `invalidateQueries`, que cancela el GET
    // en vuelo y pide otro. `refetchStatus` (cancelRefetch: false) devolvía el
    // pedido que ya estaba en curso, y ese pedido podía traer la foto previa a
    // la escritura: el requisito seguía «pendiente» hasta otro refresco.
    const refresh = () => {
      refetchRequirements();
      refreshStatusAfterWrite();
    };
    setRequirementRefetch(refresh);
    return () => setRequirementRefetch(undefined);
  }, [mocked, query.quoteID, refetchRequirements, refreshStatusAfterWrite, setRequirementRefetch]);

  const handleOfferOutcome = useCallback(async (outcome: OfferOutcome) => {
    try {
      await postOfferOutcome(query.quoteID, outcome);
    } catch (error) {
      refreshStatusAfterWrite();
      refetchOffer();
      throw error;
    }
    refreshStatusAfterWrite();
    refetchOffer();
  }, [query.quoteID, refreshStatusAfterWrite, refetchOffer]);

  const handleConfirmSession = useCallback(async () => {
    try {
      await postConfirm(query.quoteID);
    } catch (error) {
      refreshStatusAfterWrite();
      throw error;
    }
    refreshStatusAfterWrite();
  }, [query.quoteID, refreshStatusAfterWrite]);

  useEffect(() => {
    if (!mocked && query.quoteID) {
      setOfferOutcome(handleOfferOutcome);
      setConfirmSession(handleConfirmSession);
      return () => {
        setOfferOutcome(undefined);
        setConfirmSession(undefined);
      };
    }
    setOfferOutcome(undefined);
    setConfirmSession(undefined);
    return undefined;
  }, [mocked, query.quoteID, handleOfferOutcome, handleConfirmSession, setOfferOutcome, setConfirmSession]);

  if (!mocked && live.loading) {
    return (
      <section className="screen on" data-screen="overview" id="screen-ov">
        <div className="ov-head">
          <div>
            <h1>{t('boardLoading')}</h1>
          </div>
        </div>
      </section>
    );
  }

  /**
   * `live.error` con `live.data` puesto es un REFETCH que agotó sus
   * reintentos (p. ej. tras confirmar), no la carga inicial. Antes de esto,
   * cualquier refetch fallido — incluido el que sigue a una confirmación
   * exitosa — tapaba el tablero entero con esta pantalla, aunque la
   * escritura ya se hubiera registrado en el servidor (hallazgo del review
   * de OUW-70). Con datos en mano, el tablero se queda dibujado con lo
   * último conocido; `ApprovedPanel` es quien decide mostrar un aviso chico
   * y un reintento en la fila que corresponde.
   */
  if (!mocked && (live.notFound || (live.error && !live.data) || !query.quoteID)) {
    return (
      <section className="screen on" data-screen="overview" id="screen-ov">
        <div className="ov-head">
          <div>
            <h1>{live.error ? t('boardLoadError') : t('boardNotFound')}</h1>
            <p className="meta">{t('boardNotFoundBody')}</p>
            {live.error ? (
              <div className="acts" style={{ marginTop: 16 }}>
                <PortalButton variant="secondary" label={t('retry')} data-spec="o-board-retry" data-wired onClick={live.refetch} />
              </div>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="screen on" data-screen="overview" id="screen-ov">
      {/* OUW-326: el estado va pegado al número de solicitud y el detalle del
          producto, en su propia línea debajo. */}
      <div className="ov-head">
        <div>
          <h1>{t('boardHello', { name: helloName })}</h1>
          <div className="ov-ref">
            <p className="meta">
              Solicitud <b>{ref}</b>
            </p>
            <StatusPill chrome={boardChrome} />
          </div>
          <p className="meta ov-product">{`Vida a término, ${term} · ${coverage}`}</p>
        </div>
      </div>
      <Stepper chrome={boardChrome} sentOn={mocked ? null : application?.createdAt} />
      <div className="ov-grid">
        <div className="ov-main">
          <StatePanel
          awaitingSignature={awaitingSignature}
            state={state}
            application={application}
            live={!mocked}
            offer={offer.data}
            offerLoading={offer.loading}
            offerError={offer.error}
            offerSettled={offer.settled}
            onRetryOffer={offer.refetch}
            labels={offer.labels}
            approvedCta={approvedCta}
            awaitingPaymentLink={awaitingPaymentLink}
            payConfirming={payConfirming}
            payRefreshFailed={payRefreshFailed}
            onPay={handlePay}
            processingStalled={processingStalled}
            statusRefreshing={live.refreshing}
            statusError={live.error}
            onRetryStatus={live.refetch}
            requirements={requirements.data}
            requirementsLoading={requirements.loading}
            requirementsError={requirements.error}
            requirementsSettled={requirements.settled}
            onRetryRequirements={requirements.refetch}
          />
        </div>
        <div className="ov-side">
          <SummaryCard application={application} />
          <AgentCard application={application} />
        </div>
      </div>
      {signedNotice ? (
        <PortalModal
          testId="cp-signed-notice"
          title={t('signedNoticeTitle')}
          subtitle={t('signedNoticeBody')}
          closeLabel={t('close')}
          onClose={() => setSignedNotice(false)}
          footer={
            <>
              <PortalModalSpacer />
              <PortalButton label={t('signedNoticeCta')} onClick={() => setSignedNotice(false)} />
            </>
          }
        />
      ) : null}
    </section>
  );
}
